import type {
  CalendlyBookingHealth,
  CalendlyDurationHealth,
} from '../../types';

/**
 * MED-1365 (QA round 2): the words for how each session length books. A
 * length whose Calendly event type is off, or not mapped, still books online:
 * Medley sends a Google Calendar invite from the Medley calendar. The words
 * never tell the coach to upgrade or to pay.
 */

/** The session lengths that a Calendly event type maps to. */
export type MappedLength = 20 | 45 | 60;

/**
 * MED-1365 QA round 6: a free or trial Calendly plan maps only the 20 min
 * Intro and Midpoint length. Its 45 and 60 min 1:1s use the coach's Calendly
 * availability and come as Google Calendar invites.
 */
export function isLimitedPlan(
  planStage: CalendlyBookingHealth['plan_stage'] | undefined
): boolean {
  return planStage === 'free' || planStage === 'trial';
}

/** QA round 6: the lengths that a coach maps on this plan. */
export function mappedLengths(
  planStage: CalendlyBookingHealth['plan_stage'] | undefined
): MappedLength[] {
  return isLimitedPlan(planStage) ? [20] : [20, 45, 60];
}

/**
 * QA round 6: the lengths of a booking health that its plan maps. A health
 * that an older API cached can still hold 45 and 60 min on a free plan.
 */
export function planDurations(
  health: CalendlyBookingHealth | null | undefined
): CalendlyDurationHealth[] | null {
  const durations = health?.durations;
  if (!durations) return null;
  const lengths = mappedLengths(health.plan_stage);
  return durations.filter(d => lengths.includes(d.duration_minutes));
}

/** QA round 6: the note for a free or trial plan. */
export function limitedPlanNote(audience: 'coach' | 'admin'): string {
  return audience === 'coach'
    ? 'Medley uses your Calendly availability to offer the 45 and 60 min 1:1 times. These sessions come as Google Calendar invites.'
    : "Medley uses the coach's Calendly availability to offer the 45 and 60 min 1:1 times. These sessions come as Google Calendar invites.";
}

/**
 * QA round 6: what to do when a free or trial plan has no 20 min event
 * type mapped. A free plan has one event type, so the coach replaces it.
 *
 * QA round 7: when the coach has a 20 min event type that is off, the
 * prompt names it and asks the coach to turn it on. On a free plan only one
 * event type is on, so turning it on can replace the one that is on now.
 */
export function twentyMinutePrompt(
  planStage: CalendlyBookingHealth['plan_stage'] | undefined,
  audience: 'coach' | 'admin',
  offTwentyNames: string[] = []
): string {
  const names = offTwentyNames.map(n => `"${n}"`).join(' or ');
  const step = names
    ? `turn on ${audience === 'coach' ? 'your' : 'the'} ${names} event type in Calendly`
    : planStage === 'free'
      ? 'replace the event type in Calendly with a 20 min event type'
      : 'add a 20 min event type in Calendly';
  const freeNote =
    names && planStage === 'free'
      ? ' A free plan keeps only one event type on, so this can replace the event type that is on now.'
      : '';
  return audience === 'coach'
    ? `Medley uses a 20 min Calendly event type for the Intro and Midpoint check-ins. To use one, ${step}. Then map it to 20 min sessions.${freeNote}`
    : `Medley uses a 20 min Calendly event type for the Intro and Midpoint check-ins. Ask the coach to ${step}, then map it to 20 min sessions.${freeNote}`;
}

/**
 * MED-1365 QA round 9: the short step for the Needs Attention card, which
 * shows three lines. The profile card and the mapping window keep the full
 * prompt (twentyMinutePrompt).
 */
export function twentyMinuteCardText(
  planStage: CalendlyBookingHealth['plan_stage'] | undefined,
  offTwentyNames: string[] = []
): string {
  const names = offTwentyNames.map(n => `"${n}"`).join(' or ');
  if (names) return `Turn on your ${names} event type in Calendly, then map it here.`;
  if (planStage === 'free') {
    return 'Replace your Calendly event type with a 20 min one, then map it here.';
  }
  return 'Add a 20 min event type in Calendly, then map it here.';
}

/**
 * MED-1365 QA round 9: the required questions that stop a Calendly booking.
 * A question counts only on a length that the plan maps (QA round 6). A free
 * plan takes no booking through Calendly, so no question counts (QA round 3).
 * The profile card and the coach dashboard both use this, so they agree.
 */
export function blockingQuestions(
  health: CalendlyBookingHealth | null | undefined
): CalendlyBookingHealth['required_questions'] {
  if (!health || health.plan_stage === 'free') return [];
  const lengths: number[] = mappedLengths(health.plan_stage);
  return health.required_questions
    .map(item => ({
      ...item,
      durations: item.durations.filter(d => lengths.includes(d)),
    }))
    .filter(item => item.durations.length > 0);
}

/**
 * MED-1365 QA round 9: true when the 20 min length has no usable Calendly
 * event type: none is mapped, the mapped one has another length, or it is
 * off. The coach dashboard then asks the coach to map one, on any plan.
 */
export function twentyMinuteNeedsMapping(
  health: CalendlyBookingHealth | null | undefined
): boolean {
  const twenty = health?.durations?.find(d => d.duration_minutes === 20);
  return (
    twenty?.state === 'unmapped' ||
    twenty?.state === 'wrong_length' ||
    twenty?.state === 'off'
  );
}

/**
 * QA round 6: the event types that a length can map to: only the types of
 * exactly that duration.
 */
export function eventTypesForLength<T extends { duration: number }>(
  eventTypes: T[],
  length: MappedLength
): T[] {
  return eventTypes.filter(et => et.duration === length);
}

/** "20 min", "20 and 45 min" or "20, 45 and 60 min". */
export function formatDurations(durations: number[]): string {
  if (durations.length <= 1) return `${durations.join('')} min`;
  return `${durations.slice(0, -1).join(', ')} and ${durations[durations.length - 1]} min`;
}

/** The lengths that book with a Google invite because of their event type. */
function inviteDurations(
  durations: CalendlyDurationHealth[],
  state: 'off' | 'unmapped' | 'refused' | 'wrong_length'
): number[] {
  return durations.filter(d => d.state === state).map(d => d.duration_minutes);
}

/**
 * The sentences for the lengths that come as Google Calendar invites, or
 * null when every length books through Calendly (or the state is unknown).
 * healthIssues does not show them on a free plan.
 * For example: "20 and 45 min sessions come as Google Calendar invites from
 * the Medley calendar, because those Calendly event types are off."
 */
export function inviteDurationsSentences(
  health: CalendlyBookingHealth | null | undefined
): string | null {
  const durations = planDurations(health);
  if (!durations) return null;
  const sentences: string[] = [];
  const off = inviteDurations(durations, 'off');
  if (off.length > 0) {
    sentences.push(
      `${formatDurations(off)} sessions come as Google Calendar invites from the Medley calendar, because ${
        off.length === 1
          ? 'that Calendly event type is'
          : 'those Calendly event types are'
      } off.`
    );
  }
  const refused = inviteDurations(durations, 'refused');
  if (refused.length > 0) {
    sentences.push(
      `${formatDurations(refused)} sessions come as Google Calendar invites from the Medley calendar, because Calendly will not book ${
        refused.length === 1 ? 'that event type' : 'those event types'
      }.`
    );
  }
  const unmapped = inviteDurations(durations, 'unmapped');
  if (unmapped.length > 0) {
    sentences.push(
      `${formatDurations(unmapped)} sessions come as Google Calendar invites from the Medley calendar, because no Calendly event type is mapped for ${
        unmapped.length === 1 ? 'it' : 'them'
      }.`
    );
  }
  const wrongLength = inviteDurations(durations, 'wrong_length');
  if (wrongLength.length > 0) {
    sentences.push(
      `${formatDurations(wrongLength)} sessions come as Google Calendar invites from the Medley calendar, because the mapped Calendly event ${
        wrongLength.length === 1 ? 'type has' : 'types have'
      } another length.`
    );
  }
  return sentences.length > 0 ? sentences.join(' ') : null;
}

/** One row of the per-length list on the coach's Connections card. */
export interface DurationRow {
  key: number;
  /** "20 min". */
  length: string;
  /** "Calendly" or "Google Calendar invite". Null when unknown. */
  how: string | null;
  /** The event type name, "Event type off in Calendly", or "No event type mapped". */
  detail: string;
  /** True when this length books through Calendly. */
  onCalendly: boolean;
}

export function durationRows(
  durations: CalendlyDurationHealth[],
  /**
   * The Calendly plan stage. On a free plan Calendly refuses every booking
   * through Medley, so an event type that is on books as an invite too.
   */
  planStage: CalendlyBookingHealth['plan_stage'] = null
): DurationRow[] {
  return durations.map(d => {
    const length = `${d.duration_minutes} min`;
    switch (d.state) {
      case 'calendly':
        if (planStage === 'free') {
          return {
            key: d.duration_minutes,
            length,
            how: 'Google Calendar invite',
            detail: `${d.event_type_name ?? 'Event type on in Calendly'} (free Calendly plan)`,
            onCalendly: false,
          };
        }
        return {
          key: d.duration_minutes,
          length,
          how: 'Calendly',
          detail: d.event_type_name ?? 'Event type on in Calendly',
          onCalendly: true,
        };
      case 'off':
        return {
          key: d.duration_minutes,
          length,
          how: 'Google Calendar invite',
          detail: d.event_type_name
            ? `${d.event_type_name} (off in Calendly)`
            : 'Event type off in Calendly',
          onCalendly: false,
        };
      case 'refused':
        return {
          key: d.duration_minutes,
          length,
          how: 'Google Calendar invite',
          detail: d.event_type_name
            ? `${d.event_type_name} (Calendly will not book it)`
            : 'Mapped event type (not found in Calendly)',
          onCalendly: false,
        };
      case 'unmapped':
        return {
          key: d.duration_minutes,
          length,
          how: 'Google Calendar invite',
          detail: 'No event type mapped',
          onCalendly: false,
        };
      case 'wrong_length':
        return {
          key: d.duration_minutes,
          length,
          how: 'Google Calendar invite',
          detail: `${d.event_type_name ?? 'The mapped event type'} is ${
            d.event_type_duration ? `${d.event_type_duration} min` : 'another length'
          }, not ${d.duration_minutes} min`,
          onCalendly: false,
        };
      default:
        return {
          key: d.duration_minutes,
          length,
          how: null,
          detail: 'Calendly did not answer. Try again later.',
          onCalendly: false,
        };
    }
  });
}

/**
 * MED-1365 QA round 3: the label of an event type in the mapping window.
 * "Coaching (45 min, solo)", or "Medley check-in (20 min) (solo)" when the
 * name already gives the length, so the length is not said twice. An event
 * type that is off ends "(off in Calendly)".
 */
export function eventTypeOptionLabel(et: {
  name: string;
  duration: number;
  kind: string;
  active?: boolean;
}): string {
  // "20 min", "20-min", "20 mins", "20 minutes"; not "20 minds".
  const namesLength = new RegExp(
    `\\b${et.duration}\\s*-?\\s*min(?:ute)?s?\\b`,
    'i'
  ).test(et.name);
  const details = namesLength
    ? `(${et.kind})`
    : `(${et.duration} min, ${et.kind})`;
  return `${et.name} ${details}${et.active === false ? ' (off in Calendly)' : ''}`;
}
